/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import type { ShopperBasketsV2 } from '@/scapi';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { resourceRoutes } from '@/route-paths';
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useBasketUpdater } from '@/providers/basket';
import { setMiniCartOpen } from '@/hooks/mini-cart-store';
import { useToast } from '@/components/toast';
import { useSegmentRefresh } from '@/providers/segmentation';
import { ErrorCode } from '@/lib/error-codes';

interface PlpAddToCartButtonProps {
    /** Variant or standard product ID sent to cart-item-add */
    productId: string;
    productName: string;
    className?: string;
    tabIndex?: number;
}

/**
 * B2B PLP add-to-cart control — plus icon that posts directly to cart-item-add (no quick-add modal).
 */
export function PlpAddToCartButton({ productId, productName, className, tabIndex }: PlpAddToCartButtonProps) {
    const { t } = useTranslation('product');
    const fetcher = useItemFetcher({ itemId: productId, componentName: 'plp-add-to-cart' });
    const updateBasket = useBasketUpdater();
    const { addToast } = useToast();
    const refreshSegment = useSegmentRefresh();
    const [isAdding, setIsAdding] = useState(false);

    const isSubmitting = isAdding || fetcher.state !== 'idle';
    const label = isSubmitting ? t('addingToCart') : t('addToCart');

    useEffect(() => {
        if (!isAdding) return;
        if (fetcher.data?.success && fetcher.data.basket) {
            updateBasket(fetcher.data.basket as unknown as ShopperBasketsV2.schemas['Basket']);
            setIsAdding(false);
            setMiniCartOpen(true);
            return;
        }
        if (fetcher.data?.success === false) {
            const error = fetcher.data.error;
            if (error?.code === ErrorCode.SEGMENT_RESTRICTED) {
                refreshSegment();
                addToast(error.message, 'error');
            } else {
                addToast(t('failedToAddToCart', { error: error?.message }), 'error');
            }
            setIsAdding(false);
        }
    }, [isAdding, fetcher.data, updateBasket, addToast, t, refreshSegment]);

    const handleClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (!productId || isSubmitting) return;

            setIsAdding(true);
            void fetcher.submit(
                {
                    productItem: JSON.stringify({ productId, quantity: 1 }),
                },
                {
                    method: 'POST',
                    action: resourceRoutes.cartItemAdd,
                }
            );
        },
        [fetcher, isSubmitting, productId]
    );

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('cursor-pointer', className)}
            aria-label={`${label} ${productName}`}
            data-testid="plp-add-to-cart-icon"
            tabIndex={tabIndex}
            disabled={!productId || isSubmitting}
            onClick={handleClick}>
            {isSubmitting ? <Loader2 className="size-5 animate-spin" aria-hidden /> : <Plus className="size-5" aria-hidden />}
        </Button>
    );
}
